/**
 * PINs the till can check with no connection.
 *
 * Online, a PIN is checked by the server, which is the only place PINs live.
 * Offline there is nobody to ask, and a till that cannot be unlocked is a till
 * that cannot sell, so the tablet remembers the PINs that have unlocked it:
 * each time a PIN works online, the tablet keeps a slow one-way check of it
 * (PBKDF2, with a salt of the tablet's own) beside whose it was. Offline, a
 * PIN typed at the lock screen is put through the same and compared.
 *
 * WHAT IT DOES NOT KEEP
 *
 * The PIN itself, or anything the server holds. The check is made here from
 * the digits somebody just typed and the server accepted. It is no key to
 * anything: what a stolen tablet really holds is its signed-in session, which
 * can do far more than a PIN.
 *
 * HOW LONG
 *
 * Fourteen days from the last time that PIN worked online. Somebody who has
 * left, or changed their PIN, is still let in offline until then on a tablet
 * they used; everybody else stops being remembered on their own. Signing the
 * tablet out forgets all of them.
 *
 * Guesses are slowed down (`waitSeconds`): four digits are only ten thousand
 * tries, and the server's own limit is not there to stop them.
 */

const KEY = 'pos.offline.pins';
const ITERATIONS = 150_000;
export const KEEP_DAYS = 14;

/** One person who has unlocked this tablet online. */
export interface RememberedPin {
  userId: string;
  /** How the till names them: their sign-in. */
  label: string;
  /** PBKDF2-SHA-256 of the PIN with the tablet's salt, base64. */
  check: string;
  /** When the PIN last worked online, ISO 8601. */
  at: string;
}

export interface PinBook {
  salt: string;
  pins: RememberedPin[];
  /** Wrong offline tries in a row, and when the last one was. */
  failures: number;
  failedAt: string | null;
}

const toBase64 = (bytes: Uint8Array) => btoa(String.fromCharCode(...bytes));
const fromBase64 = (text: string) => Uint8Array.from(atob(text), (c) => c.charCodeAt(0));

export function newBook(): PinBook {
  return {
    salt: toBase64(crypto.getRandomValues(new Uint8Array(16))),
    pins: [],
    failures: 0,
    failedAt: null,
  };
}

/** The one-way check of a PIN, with this tablet's salt. */
export async function checkOf(pin: string, salt: string): Promise<string> {
  const key = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(pin),
    'PBKDF2',
    false,
    ['deriveBits'],
  );
  const bits = await crypto.subtle.deriveBits(
    { name: 'PBKDF2', hash: 'SHA-256', salt: fromBase64(salt), iterations: ITERATIONS },
    key,
    256,
  );
  return toBase64(new Uint8Array(bits));
}

/** Those still remembered at `now`. */
export function current(book: PinBook, now: Date): RememberedPin[] {
  const oldest = now.getTime() - KEEP_DAYS * 86_400_000;
  return book.pins.filter((p) => new Date(p.at).getTime() >= oldest);
}

/** The book with this person's PIN remembered as of `now`, replacing any they had. */
export async function remembered(
  book: PinBook,
  pin: string,
  who: { userId: string; label: string },
  now: Date,
): Promise<PinBook> {
  const check = await checkOf(pin, book.salt);
  const others = current(book, now).filter((p) => p.userId !== who.userId && p.check !== check);
  return { ...book, pins: [...others, { ...who, check, at: now.toISOString() }] };
}

/** Same length, every byte compared: how long it takes says nothing about how close it was. */
function same(a: string, b: string): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
  return diff === 0;
}

/** Whose PIN this is, if it is one this tablet remembers. */
export async function whose(
  book: PinBook,
  pin: string,
  now: Date,
): Promise<RememberedPin | null> {
  const pins = current(book, now);
  if (pins.length === 0) return null;
  const check = await checkOf(pin, book.salt);
  return pins.find((p) => same(p.check, check)) ?? null;
}

/**
 * How long the lock screen waits after a wrong PIN before taking another:
 * nothing for the first few slips, then doubling to a quarter of an hour.
 */
export function waitSeconds(failures: number): number {
  if (failures < 5) return 0;
  return Math.min(15 * 60, 30 * 2 ** (failures - 5));
}

/** Seconds still to wait at `now`, after the book's last wrong try. */
export function waitLeft(book: PinBook, now: Date): number {
  if (!book.failedAt) return 0;
  const since = (now.getTime() - new Date(book.failedAt).getTime()) / 1000;
  return Math.max(0, Math.ceil(waitSeconds(book.failures) - since));
}

// --------------------------------------------------------------------------- //
// On this device
// --------------------------------------------------------------------------- //

export function loadBook(): PinBook {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<PinBook>;
      if (typeof parsed.salt === 'string' && Array.isArray(parsed.pins)) {
        return {
          salt: parsed.salt,
          pins: parsed.pins,
          failures: typeof parsed.failures === 'number' ? parsed.failures : 0,
          failedAt: typeof parsed.failedAt === 'string' ? parsed.failedAt : null,
        };
      }
    }
  } catch {
    // Unreadable: start again, which only means PINs are learnt afresh.
  }
  return newBook();
}

function saveBook(book: PinBook): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(book));
  } catch {
    // Storage refused: offline unlocking will not know this PIN, nothing worse.
  }
}

/**
 * Whose a session is, read from its access token's own claims: the PIN
 * sign-in answers with tokens and nothing else. Null when they cannot be
 * read, and then the PIN is not remembered rather than remembered as nobody.
 */
export function whoFromToken(accessToken: string): { userId: string; label: string } | null {
  try {
    const part = accessToken.split('.')[1] ?? '';
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    const claims = JSON.parse(json) as { sub?: unknown; email?: unknown };
    if (typeof claims.sub !== 'string' || !claims.sub) return null;
    return {
      userId: claims.sub,
      label: typeof claims.email === 'string' ? claims.email : claims.sub,
    };
  } catch {
    return null;
  }
}

/** After a PIN worked online: remember it for the next time there is no connection. */
export async function rememberPin(
  pin: string,
  tokens: { access_token: string },
): Promise<void> {
  const who = whoFromToken(tokens.access_token);
  if (!who) return;
  const next = await remembered(loadBook(), pin, who, new Date());
  saveBook({ ...next, failures: 0, failedAt: null });
}

/** Signed out: the tablet is somebody else's now, and forgets every PIN. */
export function forgetPins(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    // Nothing kept, nothing to forget.
  }
}

/** Offline, at the lock screen: whose PIN this is, counting the wrong ones. */
export async function unlockOffline(
  pin: string,
): Promise<{ who: RememberedPin } | { wait: number } | { wrong: true }> {
  const book = loadBook();
  const now = new Date();
  const wait = waitLeft(book, now);
  if (wait > 0) return { wait };
  const who = await whose(book, pin, now);
  if (who) {
    saveBook({ ...book, failures: 0, failedAt: null });
    return { who };
  }
  saveBook({ ...book, failures: book.failures + 1, failedAt: now.toISOString() });
  return { wrong: true };
}

/** Whether any PIN can be checked here without a connection. */
export function anyRemembered(): boolean {
  return current(loadBook(), new Date()).length > 0;
}
